import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { Heading } from '../../components';

import { FaCar, FaComment, FaCog, FaDollarSign, FaExchangeAlt, FaMoneyCheckAlt, FaPalette, FaCalendar, FaThermometerHalf, FaAngleDown } from 'react-icons/fa';
import { FaUser, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';

import axios from "axios";

// API endpoint for submitting the quote form
const API_PATH = 'https://primecars-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const GetQuote = () => {

  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    contactMethod: '',
    carMake: '',
    carModel: '',
    vehicleColor: '',
    financing: '',
    downPayment: '',
    tradeIn: '',
    tradeInMake: '',
    tradeInModel: '',
    tradeInYear: '',
    tradeInCondition: '',
    comments: ''
  });
  
  const [showTradeInFields, setShowTradeInFields] = useState(false); // State for conditional rendering of trade-in fields
  const [mailSent, setMailSent] = useState(false); // State for form submission status
  const [error, setError] = useState(''); // State for error messages
  const [isSubmitting, setIsSubmitting] = useState(false); // State for form submission loading state

  // Effect to hide the success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  /**
   * Handles form submission.
   * Validates form data and sends it to the server.
   */
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validate form fields
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.contactMethod) validationErrors.push('Contact method is required');
      if (!formData.carMake.trim()) validationErrors.push('Car make is required');
      if (!formData.carModel.trim()) validationErrors.push('Car model is required');
      if (!formData.vehicleColor.trim()) validationErrors.push('Vehicle color is required');
      if (!formData.financing) validationErrors.push('Financing option is required');
      if (!formData.downPayment) validationErrors.push('Down payment is required');

      // Throw error if validation fails
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send form data to the server
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle server response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Quote request submission failed');
      }

      // Reset form and show success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        contactMethod: '',
        carMake: '',
        carModel: '',
        vehicleColor: '',
        financing: '',
        downPayment: '',
        tradeIn: '',
        tradeInMake: '',
        tradeInModel: '',
        tradeInYear: '',
        tradeInCondition: '',
        comments: ''
      });
    } catch (err) {
      // Handle errors
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset loading state
      setIsSubmitting(false);
    }
  };

  /**
   * Handles input changes and updates form state.
   * Also handles conditional rendering of trade-in fields.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tradeIn') {
      setShowTradeInFields(value === 'yes'); // Show/hide trade-in fields
    }
    setFormData(prev => ({ ...prev, [name]: value })); // Update form state
  };

  return (
    <section className="custom-form get-quote linear-bg"> 
      <div className="box-container">
        <div className="content">
          <form className='dark-form' onSubmit={handleSubmitForm}>

            {/* Form heading */}
            <Heading title={'Request a Quote'} subTitle={'Get a Quote'} sideHeading linear />

            <div className='input-box'>

              {/* Name Input */}
              <div className='input-field'>
                <label htmlFor="name"><FaUser /></label>
                <input type="text" className='box' id="name" name="name" 
                  value={formData.name} onChange={handleInputChange} 
                  placeholder="Full Name" disabled={isSubmitting} required />
              </div>

              {/* Email Input */}
              <div className='input-field'>
                <label htmlFor="email"><FaEnvelope /></label>
                <input type="email" className='box' id="email" name="email" 
                  value={formData.email} onChange={handleInputChange} 
                  placeholder="Email Address" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Phone field */}
              <div className='input-field'>
                <label htmlFor="number"><FaPhoneAlt /></label>
                <input type="tel" className='box' id="number" name="number" 
                  value={formData.number} onChange={handleInputChange} 
                  placeholder="Phone Number" disabled={isSubmitting} required />
              </div>

              {/* Contact Method fields */}
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="contactMethod"><FaCog /></label>
                  <select className='select-box' id="contactMethod" name="contactMethod" 
                    value={formData.contactMethod} onChange={handleInputChange} 
                    disabled={isSubmitting} required >
                    <option value="">Preferred Contact Method</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                  </select>
                  <div className='icon-container'><FaAngleDown className='icon' /></div>
                </div>
              </div>

            </div>

            <div className='input-box'>

              {/* Car Make field */}
              <div className='input-field'>
                <label htmlFor="carMake"><FaCar /></label>
                <input type="text" className='box' id="carMake" name="carMake" 
                  value={formData.carMake} onChange={handleInputChange} 
                  placeholder="Car Make" disabled={isSubmitting} required />
              </div>

              {/* Car Model field */}
              <div className='input-field'>
                <label htmlFor="carModel"><FaCar /></label>
                <input type="text" className='box' id="carModel" name="carModel" 
                  value={formData.carModel} onChange={handleInputChange} 
                  placeholder="Car Model" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Vehicle Color field */}
              <div className='input-field'>
                <label htmlFor="vehicleColor"><FaPalette /></label>
                <input type="text" className='box' id="vehicleColor" name="vehicleColor" 
                  value={formData.vehicleColor} onChange={handleInputChange} 
                  placeholder="Vehicle Color" disabled={isSubmitting} required />
              </div>

              {/* Financing field */}
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="financing"><FaDollarSign /></label>
                  <select className='select-box' id="financing" name="financing" 
                    value={formData.financing} onChange={handleInputChange} 
                    disabled={isSubmitting} required >
                    <option value="">Preferred Financing</option>
                    <option value="cash">Cash</option>
                    <option value="loan">Loan</option>
                    <option value="lease">Lease</option>
                  </select>
                  <div className='icon-container'><FaAngleDown className='icon' /></div>
                </div>
              </div>
            </div>

            <div className='input-box'>

              {/* Down Payment field */}
              <div className='input-field'>
                <label htmlFor="downPayment"><FaMoneyCheckAlt /></label>
                <input type="number" className='box' id="downPayment" name="downPayment" 
                  value={formData.downPayment} onChange={handleInputChange} 
                  placeholder="Down Payment Amount" disabled={isSubmitting} required />
              </div>

              {/* Trade-In fields */}
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="tradeIn"><FaExchangeAlt /></label>
                  <select className='select-box' id="tradeIn" name="tradeIn" 
                    value={formData.tradeIn} onChange={handleInputChange} 
                    disabled={isSubmitting} required >
                    <option value="">Trade-In Vehicle?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <div className='icon-container'><FaAngleDown className='icon' /></div>
                </div>
              </div>
            </div>

            {/* Conditional Trade-In fields */}
            {showTradeInFields && (
              <div id="trade-in-fields">
                <div className='input-box'>
                  <div className='input-field'>
                    <label htmlFor="tradeInMake"><FaCar /></label>
                    <input type="text" className='box' id="tradeInMake" name="tradeInMake" 
                      value={formData.tradeInMake} onChange={handleInputChange} 
                      placeholder="Trade-In Make" disabled={isSubmitting} required />
                  </div>

                  <div className='input-field'>
                    <label htmlFor="tradeInModel"><FaCar /></label>
                    <input type="text" className='box' id="tradeInModel" name="tradeInModel" 
                      value={formData.tradeInModel} onChange={handleInputChange} 
                      placeholder="Trade-In Model" disabled={isSubmitting} required />
                  </div>
                </div>

                <div className='input-box'>
                  <div className='input-field'>
                    <label htmlFor="tradeInYear"><FaCalendar /></label>
                    <input type="text" className='box' id="tradeInYear" name="tradeInYear" 
                      value={formData.tradeInYear} onChange={handleInputChange} 
                      placeholder="Trade-In Year" disabled={isSubmitting} required />
                  </div>

                  <div className='select'>
                    <div className="select-container">
                      <label htmlFor="tradeInCondition"><FaThermometerHalf /></label>
                      <select className='select-box' id="tradeInCondition" name="tradeInCondition" 
                        value={formData.tradeInCondition} onChange={handleInputChange} 
                        disabled={isSubmitting} required >
                        <option value="">Condition</option>
                        <option value="excellent">Excellent</option>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="poor">Poor</option>
                      </select>
                      <div className='icon-container'><FaAngleDown className='icon' /></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Comments field */}
            <div className='input-field text-area'>
              <label htmlFor="comments"><FaComment /></label>
              <textarea cols="30" rows="10" id="comments" className='box' 
                name="comments" value={formData.comments} onChange={handleInputChange} 
                placeholder="Additional Comments" disabled={isSubmitting} required></textarea>
            </div>

            {/* Submit button */}
            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Get Quote'}
            </button>

            {/* Error and success messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote request submitted</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;